import { useTrackBackendEventOnMount } from '@pm/analytics';
import { useGetDetailedLabResultQuery } from '@pm/graphql';
import { Text } from '@pm/ui';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { LoadingPage } from '../../LoadingPage';
import { LabResultDownload } from './LabResultDownload';
import { LabSection } from './LabSection';
import { getLabResultsCategorization, getSortedCategories } from './constants';

type LabDetailPageProps = {
  supportLink: string;
};

export const LabDetailPage = ({ supportLink }: LabDetailPageProps) => {
  const { t } = useTranslation('labs');
  const { id } = useParams<{ id: string }>();

  useTrackBackendEventOnMount('lab_results_accessed_by_patient', {
    view_mode: 'detailed',
    detailed_lab_result_id: id!,
  });

  const { data, loading, error } = useGetDetailedLabResultQuery({
    variables: { id: id! },
    skip: !id,
  });

  if (loading) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <div className="p-m">
        <Text size="bodyL" color="contentWarning">
          {t('LabWorkLoadError')}
        </Text>
      </div>
    );
  }

  const result = data?.detailedLabResult;

  if (!result) {
    return (
      <div className="p-m">
        <Text size="bodyL" color="contentSubdued">
          {t('ResultText.ResultsUnavailable')}
        </Text>
      </div>
    );
  }

  const { detailedLabResult, documentBasedLabResult, labKit } = result;
  const providerName = detailedLabResult.providerName;

  const categorizedObservations = getLabResultsCategorization(
    detailedLabResult.observations,
  );

  return (
    <div className="space-y-s pb-m">
      {labKit && documentBasedLabResult && (
        <LabResultDownload
          documentBasedLabResultId={documentBasedLabResult.id}
          orderedAt={labKit.createdAt}
          documentUploadedAt={documentBasedLabResult.documentUploadedAt}
          outboundShipment={labKit.outboundShipment ?? null}
          inboundShipment={labKit.inboundShipment ?? null}
          supportLink={supportLink}
        />
      )}

      <Text size="bodyS" color="contentSubdued">
        {t('ClinicalSupportMessage')}
      </Text>

      {/* <LabSummary
        observations={detailedLabResult.observations.map((obs) => {
          const providerName = observationProviderMap.get(obs.id);
          const internalName = getInternalName(
            providerName,
            obs.providerTestIdentifier,
          );
          const { title } = getObservationInfo(internalName, obs.testName, t);
          return { ...obs, displayName: title };
        })}
      />
      <LabMissingResults observations={detailedLabResult.observations} /> */}

      {getSortedCategories().map((category) => {
        const observations = categorizedObservations[category];
        if (!observations || observations.length === 0) return null;
        return (
          <LabSection
            key={category}
            category={category}
            observations={observations}
            providerName={providerName}
          />
        );
      })}
    </div>
  );
};
